/**
 * Hook para buscar dados de páginas de entrada (landing pages) do Google Analytics 4.
 *
 * ## Um objeto de parâmetros, e não dez posicionais
 *
 * Os dois consumidores da rota pedem recortes diferentes — a página `/dashboard/pages` quer o
 * tráfego inteiro, a aba PÁGINAS DE ENTRADA do SEO quer só o orgânico —, e cada opção nova
 * empurrava mais um `undefined` de enchimento para a chamada de quem não a usa. Com dois
 * chamadores, o objeto é o formato que não cobra esse pedágio.
 *
 * ## `filterChannelGroup` — o recorte de canal, quando a tela pede um
 *
 * Vai como `filter_channel_group`, o cross-filter de M-205, que a rota traduz para um filtro
 * de dimensão sobre `sessionDefaultChannelGroup` na própria Data API — antes de agregar e
 * antes de paginar, de modo que as linhas, o rodapé e a paginação descrevem o mesmo recorte.
 *
 * ⚠️ O valor é o **rótulo cru do GA4** (`Organic Search`, em `CANAL_ORGANICO`), o mesmo que
 * `SeoCardsService` usa para os cards de sessões e pedidos orgânicos. Divergir dele faria a
 * tabela e os cards da mesma tela somarem universos diferentes, que é o defeito que o item 19
 * da auditoria conserta.
 */
import { useState, useEffect } from "react";
import { getLandingPagesApiV1PagesLandingPageGet } from "@/backend-sdk/client/sdk.gen";
import type {
  PagesLandingPageItem,
  PagesLandingPageTotals,
} from "@/backend-sdk/client/types.gen";
import { isHeyApiError } from "@/lib/hey-api-client";
import type { TableQueryDTO } from "@/types/table-query";

type LandingPageData = PagesLandingPageItem;
type LandingPageTotals = PagesLandingPageTotals;

export interface LandingPageDataParams {
  readonly startDate: string;
  readonly endDate: string;
  readonly comparison: string;
  readonly customerUuid?: string;
  readonly accountId?: string;
  readonly page?: number;
  readonly pageSize?: number;
  readonly tableQuery?: TableQueryDTO;
  /** Muda a cada gravação dos filtros de campanha, para forçar a rebusca. */
  readonly campaignFiltersNonce?: number;
  /** O rótulo de `sessionDefaultChannelGroup`; ausente é "sem recorte de canal". */
  readonly filterChannelGroup?: string;
}

export const useLandingPageData = ({
  startDate,
  endDate,
  comparison,
  customerUuid,
  accountId,
  page = 1,
  pageSize = 10,
  tableQuery,
  campaignFiltersNonce,
  filterChannelGroup,
}: LandingPageDataParams) => {
  const [data, setData] = useState<LandingPageData[]>([]);
  const [totals, setTotals] = useState<LandingPageTotals | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!customerUuid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data: response } =
          await getLandingPagesApiV1PagesLandingPageGet({
            query: {
              start_date: startDate,
              end_date: endDate,
              comparison,
              customer_uuid: customerUuid,
              account_id: accountId,
              page,
              page_size: pageSize,
              q: tableQuery?.q,
              sort_by: tableQuery?.sortBy,
              sort_dir: tableQuery?.sortDir,
              filters: tableQuery?.filters?.length
                ? JSON.stringify(tableQuery.filters)
                : undefined,
              filter_channel_group: filterChannelGroup,
            },
            throwOnError: true,
          });

        setData(response.data || []);
        setTotals(response.totals || null);
        setPagination(
          response.pagination || {
            page: 1,
            page_size: 10,
            total_items: 0,
            total_pages: 0,
          },
        );
      } catch (err: unknown) {
        console.error("Erro ao buscar dados de landing pages:", err);
        setError(
          isHeyApiError(err) && typeof err.detail === "string"
            ? err.detail
            : "Erro ao buscar dados de landing pages",
        );
        setData([]);
        setTotals(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    page,
    pageSize,
    tableQuery,
    campaignFiltersNonce,
    filterChannelGroup,
  ]);

  return {
    data,
    totals,
    pagination,
    loading,
    error,
  };
};
